import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthLayout } from "@/components/PublicLayout";
import { OtpInput } from "@/components/OtpInput";
import { IsoCred } from "@/components/Brand";

export default function Login({ admin = false, dev = false }) {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const portal = dev ? "dev" : admin ? "admin" : "lab";
  const staff = admin || dev;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/login", { email, password, portal });
      setStep(2);
      toast.success(`A 6-digit code was sent to ${email}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login/verify", { email, otp, portal });
      setUser(data);
      nav(data.must_change_password ? "/change-password" : dev ? "/dev" : admin ? "/admin" : "/dashboard");
    } catch (err) { toast.error(errMsg(err)); setOtp(""); } finally { setBusy(false); }
  };
  const resend = async () => {
    try { await api.post("/auth/otp/resend", { email, purpose: dev ? "dev_login" : "login" }); toast.success("New code sent"); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <AuthLayout title={dev ? "Q log" : admin ? "Admin Login" : <>Sign in to <IsoCred /></>} subtitle={dev || admin ? "" : "Password + one-time code sent to your email."}>
      {step === 1 ? (
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" /></div>
          <div className="space-y-1.5">
            <div className="flex justify-between"><Label>Password</Label>{!staff && <Link to="/forgot-password" className="text-xs text-[#48217A] hover:underline" data-testid="forgot-password-link">Forgot password?</Link>}</div>
            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-[#48217A] hover:bg-[#3B1A66]" data-testid="login-submit-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Continue
          </Button>
          {dev ? <Link to="/qlog/reset" className="block text-center text-xs text-[#48217A] hover:underline" data-testid="qlog-forgot-link">Forgot password?</Link> : admin ? <Link to="/forgot-password" className="block text-center text-xs text-[#48217A] hover:underline" data-testid="admin-forgot-link">Forgot password?</Link>
            : <p className="text-center text-sm text-[#5A6B87]">New laboratory? <Link to="/signup" className="text-[#48217A] font-medium hover:underline" data-testid="goto-signup-link">Create an account</Link></p>}
        </form>
      ) : (
        <div className="space-y-5" data-testid="login-otp-step">
          <p className="text-sm text-[#5A6B87]">Enter the 6-digit code sent to <b className="text-[#172238]">{email}</b>. It expires in 10 minutes.</p>
          <OtpInput value={otp} onChange={setOtp} testid="login-otp-input" />
          <Button disabled={otp.length !== 6 || busy} onClick={verify} className="w-full bg-[#2E9B65] hover:bg-[#258254]" data-testid="login-otp-verify-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Verify & sign in
          </Button>
          <div className="flex justify-between text-xs">
            <button onClick={() => { setStep(1); setOtp(""); }} className="text-[#5A6B87] hover:underline" data-testid="login-back-button">Back</button>
            <button onClick={resend} className="text-[#48217A] hover:underline" data-testid="login-resend-otp-button">Resend code</button>
          </div>
        </div>
      )}
    </AuthLayout>
  );
}
